import React from 'react';
import { X, StickyNote, Save } from 'lucide-react';
import { WINDOW_LABEL, fmtDate } from './constants';
import { StatusBadge } from './shared';

const HistoryDrawer = ({ client, notesDraft, onNotesDraftChange, onSaveNotes, onClose }) => {
  if (!client) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="history-drawer">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="px-6 py-5 border-b border-black/10 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.28em] text-[#c9a961] font-semibold">CLIENT HISTORY</p>
            <h3
              className="font-serif text-2xl text-[#1c1e20]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              {client.name || client.email}
            </h3>
            <p className="text-[13px] text-[#6b6b6b] mt-1">
              {client.email}{client.phone ? ` · ${client.phone}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#6b6b6b] hover:text-[#1c1e20]"
            aria-label="Close"
            data-testid="history-drawer-close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="px-6 py-5 grid grid-cols-3 gap-3 border-b border-black/10">
          <div>
            <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold">TOTAL VISITS</p>
            <p className="font-serif text-2xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              {client.count}
            </p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold">FIRST SEEN</p>
            <p className="text-[14px] text-[#1c1e20]">{client.first_seen ? fmtDate(client.first_seen) : '—'}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold">RECENT</p>
            <p className="text-[14px] text-[#1c1e20]">
              {client.appointments?.[0] ? fmtDate(client.appointments[0].date) : '—'}
            </p>
          </div>
        </div>

        <div className="px-6 py-4">
          <h4 className="text-[11px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-4">APPOINTMENTS</h4>
          {(!client.appointments || client.appointments.length === 0) && (
            <p className="text-[#6b6b6b] text-sm">No prior appointments.</p>
          )}
          <ol className="relative border-l border-[#c9a961]/40 ml-2">
            {(client.appointments || []).map((a) => {
              const draft = notesDraft[a.id] ?? a.notes ?? '';
              return (
                <li key={a.id} className="mb-6 ml-5">
                  <span className="absolute -left-[6px] w-3 h-3 rounded-full bg-[#c9a961]" />
                  <div className="flex items-center gap-3 mb-1">
                    <span
                      className="font-serif text-lg text-[#1c1e20]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      {fmtDate(a.date) || 'No date set'}
                    </span>
                    <StatusBadge value={a.status} />
                  </div>
                  <p className="text-[13px] text-[#3c3c3c]">
                    {a.service} · {WINDOW_LABEL[a.time_window] || '—'}
                  </p>
                  {a.message && <p className="text-[12px] text-[#6b6b6b] mt-1">Client note: {a.message}</p>}

                  <div className="mt-3 bg-[#faf6f0] border border-black/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <StickyNote size={13} className="text-[#c9a961]" />
                      <span className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold">INTERNAL NOTES</span>
                    </div>
                    <textarea
                      value={draft}
                      onChange={(e) => onNotesDraftChange(a.id, e.target.value)}
                      rows={2}
                      placeholder="Add a private note about this visit (draw quality, prep, follow-ups)…"
                      data-testid={`internal-notes-input-${a.id}`}
                      className="w-full border border-black/10 px-3 py-2 text-[13px] focus:outline-none focus:border-[#c9a961] bg-white"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={() => onSaveNotes(a.id)}
                        data-testid={`save-notes-${a.id}`}
                        className="flex items-center gap-1.5 bg-[#c9a961] hover:bg-[#b89751] text-[#141618] tracking-[0.22em] text-[10px] font-semibold px-3 py-1.5"
                      >
                        <Save size={12} /> SAVE NOTE
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </div>
  );
};

export default HistoryDrawer;
